import type {ReactNode, HTMLAttributes, MouseEvent, KeyboardEvent} from 'react'
import {useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {TableRow} from '@/components/ui/table'
import {cn} from '@/lib/utils'

interface ClickableTableRowProps extends HTMLAttributes<HTMLTableRowElement> {
    to?: string
    state?: unknown
    children: ReactNode
}

const MAX_ACCESSIBLE_NAME_LENGTH = 200

export function ClickableTableRow({to, state, children, className, onClick, onKeyDown, ...rest}: ClickableTableRowProps) {
    const navigate = useNavigate()
    const rowRef = useRef<HTMLTableRowElement>(null)
    const callerAriaLabel = rest['aria-label']

    /* WHY: a clickable <tr role="link"> computes its accessible name from the
       concatenated textContent of every cell ("Jane Doe Pending 4/16/2026 7"),
       which screen-reader users hear in full on every visit. Auto-derive
       aria-label from the FIRST cell (the identity column by convention).
       Prefer the inner anchor's text when present (issue 1069 — otherwise the
       row label concatenates "<name><description>" run-on), falling back to the
       full cell textContent for cells without a leading link. aria-hidden
       descendants are always stripped so decorative icons / count badges don't
       leak in. Issues 794, 1069. */
    useEffect(() => {
        if (callerAriaLabel) return
        const row = rowRef.current
        if (!row) return
        const computeName = () => {
            const firstCell = row.querySelector('td')
            if (!firstCell) return
            const innerLink = firstCell.querySelector('a')
            const source = innerLink ?? firstCell
            const clone = source.cloneNode(true) as HTMLElement
            // eslint-disable-next-line i18next/no-literal-string -- CSS attribute selector
            clone.querySelectorAll('[aria-hidden="true"]').forEach((n) => n.remove())
            const text = (clone.textContent ?? '').trim().slice(0, MAX_ACCESSIBLE_NAME_LENGTH)
            // eslint-disable-next-line i18next/no-literal-string -- DOM attribute name
            if (text) row.setAttribute('aria-label', text)
        }
        computeName()
        const observer = new MutationObserver(computeName)
        observer.observe(row, {childList: true, subtree: true, characterData: true})
        return () => observer.disconnect()
    }, [callerAriaLabel])

    const navigateTo = () => {
        if (!to) return
        if (state !== undefined) navigate(to, {state})
        else navigate(to)
    }

    const handleClick = (e: MouseEvent<HTMLTableRowElement>) => {
        onClick?.(e)
        navigateTo()
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
        onKeyDown?.(e)
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            if (to) navigateTo()
            else onClick?.(e as unknown as MouseEvent<HTMLTableRowElement>)
        }
    }

    return (
        <TableRow
            ref={rowRef}
            role={to ? 'link' : 'button'}
            tabIndex={0}
            className={cn('cursor-pointer hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none', className)}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            {...rest}
        >
            {children}
        </TableRow>
    )
}
